import { useEffect, useMemo, useRef, useState } from 'react';
import * as XLSX from 'xlsx';
import { ClipboardCopy, Download, FileDown } from 'lucide-react';
import {
    aoaExport, tsvDepuisAoa, lignesNotesDepuisResultat, resumeNotes,
} from '../../utils/exportNotes';

/**
 * « Exporter » : LE bouton d'export de l'évaluation. Le prof choisit le
 * contenu (Notes converties sur un barème déclaré / Par compétence / Les
 * deux) puis copie le tableau (presse-papier, collable dans Excel ou le
 * logiciel de notes) ou télécharge en Excel. Popover fermé au clic
 * extérieur / Échap.
 */
const CONTENUS = [
    { cle: 'notes', label: 'Notes' },
    { cle: 'competences', label: 'Par compétence' },
    { cle: 'deux', label: 'Les deux' },
];

const ExportNotes = ({ resultatEval }) => {
    const [ouvert, setOuvert] = useState(false);
    const [contenu, setContenu] = useState('notes');
    const [bareme, setBareme] = useState('20');
    const [copie, setCopie] = useState(false);
    const [erreur, setErreur] = useState(null);
    const racineRef = useRef(null);

    useEffect(() => {
        if (!ouvert) return undefined;
        const fermerSiDehors = (e) => {
            if (racineRef.current && !racineRef.current.contains(e.target)) setOuvert(false);
        };
        const fermerEchap = (e) => {
            if (e.key === 'Escape') setOuvert(false);
        };
        document.addEventListener('mousedown', fermerSiDehors);
        document.addEventListener('keydown', fermerEchap);
        return () => {
            document.removeEventListener('mousedown', fermerSiDehors);
            document.removeEventListener('keydown', fermerEchap);
        };
    }, [ouvert]);

    const avecNotes = contenu !== 'competences';
    const baremeValide = !avecNotes || parseFloat(bareme) > 0;

    const aoa = useMemo(
        () => aoaExport(resultatEval?.eleves_resultats, parseFloat(bareme), contenu),
        [resultatEval, bareme, contenu]
    );
    const resume = useMemo(
        () => resumeNotes(lignesNotesDepuisResultat(resultatEval?.eleves_resultats, parseFloat(bareme))),
        [resultatEval, bareme]
    );

    const copier = async () => {
        setErreur(null);
        setCopie(false);
        if (!navigator.clipboard?.writeText) {
            setErreur('Presse-papier indisponible : utilisez le fichier Excel.');
            return;
        }
        try {
            await navigator.clipboard.writeText(tsvDepuisAoa(aoa));
            setCopie(true);
        } catch {
            setErreur('Copie refusée par le navigateur : utilisez le fichier Excel.');
        }
    };

    const telechargerExcel = () => {
        const titre = (resultatEval?.titre || 'evaluation')
            .replace(/[^\wà-ÿÀ-Ý -]/g, '')
            .trim()
            .replace(/\s+/g, '_');
        const prefixe = contenu === 'notes' ? 'notes' : contenu === 'competences' ? 'competences' : 'export';
        const feuille = XLSX.utils.aoa_to_sheet(aoa);
        const classeur = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(classeur, feuille, 'Résultats');
        XLSX.writeFile(classeur, `${prefixe}_${titre || 'evaluation'}.xlsx`);
    };

    return (
        <div ref={racineRef} className="relative">
            <button
                type="button"
                onClick={() => { setOuvert((v) => !v); setCopie(false); setErreur(null); }}
                title="Exporter l'évaluation : notes converties, détail par compétence, ou les deux"
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-[13px] font-extrabold bg-[#F2F8FC] text-blue-600 hover:bg-[#E8F3FB] transition-colors"
            >
                <FileDown size={14} />
                Exporter
            </button>

            {ouvert && (
                <div className="absolute right-0 z-40 mt-1.5 w-80 max-w-[calc(100vw-2rem)] bg-white border border-slate-200 rounded-xl shadow-[0_8px_24px_rgba(15,23,42,0.16)] p-4 space-y-3">
                    <div>
                        <p className="text-xs font-extrabold text-slate-500 uppercase tracking-wide mb-1.5">
                            Quoi exporter ?
                        </p>
                        <div className="flex flex-wrap gap-1.5">
                            {CONTENUS.map((c) => (
                                <button
                                    key={c.cle}
                                    type="button"
                                    onClick={() => { setContenu(c.cle); setCopie(false); }}
                                    className={`px-3 py-1.5 rounded-full text-xs font-extrabold border-2 transition-colors ${
                                        contenu === c.cle
                                            ? 'bg-blue-600 border-blue-600 text-white'
                                            : 'bg-white border-slate-200 text-slate-600 hover:border-blue-300'
                                    }`}
                                >
                                    {c.label}
                                </button>
                            ))}
                        </div>
                    </div>

                    {avecNotes && (
                        <div>
                            <label className="block text-xs font-extrabold text-slate-500 uppercase tracking-wide mb-1">
                                Note sur
                            </label>
                            <input
                                type="number"
                                min="1"
                                value={bareme}
                                onChange={(e) => { setBareme(e.target.value); setCopie(false); }}
                                className="w-24 border-2 border-slate-200 rounded-xl px-3 py-1.5 text-sm font-semibold focus:border-blue-400 outline-none"
                            />
                            <p className="text-[12.5px] font-semibold text-slate-500 mt-1.5">
                                {baremeValide && resume.nbNotes > 0 ? (
                                    <>
                                        Moyenne de la classe : <strong>{String(resume.moyenne).replace('.', ',')}/{bareme}</strong>
                                        {' · '}{resume.nbNotes} élève{resume.nbNotes > 1 ? 's' : ''} noté{resume.nbNotes > 1 ? 's' : ''}
                                        {resume.nbSansNote > 0 ? `, ${resume.nbSansNote} sans note` : ''}.
                                    </>
                                ) : 'Aucune case évaluée pour l\'instant.'}
                            </p>
                        </div>
                    )}

                    <div className="flex flex-col gap-2">
                        <button
                            type="button"
                            disabled={!baremeValide}
                            onClick={copier}
                            className="inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl text-[13px] font-extrabold bg-blue-600 text-white hover:bg-blue-700 disabled:bg-slate-300 transition-colors"
                        >
                            <ClipboardCopy size={14} />
                            {copie ? 'Copié ✓' : 'Copier le tableau'}
                        </button>
                        <button
                            type="button"
                            disabled={!baremeValide}
                            onClick={telechargerExcel}
                            className="inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl text-[13px] font-extrabold bg-white border-2 border-slate-200 text-slate-600 hover:border-blue-300 disabled:opacity-50 transition-colors"
                        >
                            <Download size={14} />
                            Télécharger en Excel
                        </button>
                    </div>
                    {erreur && <p className="text-[12px] font-bold text-amber-700">{erreur}</p>}
                </div>
            )}
        </div>
    );
};

export default ExportNotes;
